import React, { useCallback } from 'react';
import classNames from 'classnames';

const formatDisplayValue = (value: unknown): string => {
  if (value == null || value === '') return '';

  const num =
    typeof value === 'number' ? value : Number.parseFloat(value as string);

  return Number.isFinite(num) ? num.toLocaleString('en-US') : '0';
};

const parseNumericInput = (raw: string): number => {
  const cleaned = raw.replace(/[^0-9.-]/g, '');
  const num = Number.parseFloat(cleaned);

  return Number.isFinite(num) ? num : 0;
};

interface MatrixCellProps {
  value: number;
  prefix?: string;
  suffix?: string;
  onChange: (val: number) => void;
  onFocus: () => void;
  onBlur: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
  isFocused?: boolean;
  cellRef?: React.Ref<HTMLDivElement>;
  'data-testid'?: string;
  'aria-label'?: string;
}

const MatrixCell = ({
  value,
  prefix,
  suffix,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  readOnly,
  isFocused,
  cellRef,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
}: MatrixCellProps): React.JSX.Element => {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(parseNumericInput(e.target.value));
    },
    [onChange]
  );

  if (readOnly) {
    return (
      <div className="flex flex-1 items-center justify-center gap-1 h-10 p-2 text-sm leading-5 text-gray-700 whitespace-nowrap">
        -
      </div>
    );
  }

  return (
    <div
      ref={cellRef}
      className={classNames(
        'flex flex-1 items-center justify-center gap-1 h-10 p-2 whitespace-nowrap',
        isFocused && 'ring-2 ring-inset ring-navy-500 rounded-sm'
      )}
    >
      {prefix && (
        <span className="text-sm leading-5 text-gray-500 shrink-0">
          {prefix}
        </span>
      )}
      <input
        type="text"
        inputMode="numeric"
        value={formatDisplayValue(value)}
        onChange={handleChange}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        className="w-full min-w-0 text-sm leading-5 text-gray-700 text-center bg-transparent outline-none border-none p-0"
        aria-label={ariaLabel}
        data-testid={dataTestId}
      />
      {suffix && (
        <span className="text-sm leading-5 text-gray-500 shrink-0">
          {suffix}
        </span>
      )}
    </div>
  );
};

export default MatrixCell;
